import React, { useEffect, useState, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Popconfirm, theme, message, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { RootState } from 'store/configureStore';
import CustomTable from 'components/Tables/CustomTable';
import { managementOp } from 'store/ducks/Management';
import { CustomDatePicker } from 'components/CustomDatePicker/CustomDatePicker';
import { IAuditFindingsResult } from 'store/ducks/Management/types';
import { useUnsavedChanges } from 'utils/useUnsavedChanges';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import Helpers from 'utils/Helpers';
import i18n from 'constants/translation/i18n';
import DataManagementLayout from 'components/Layouts/DataManagementLayout/DataManagementLayout';
import CustomSelect from 'components/CustomSelect';
import * as Styled from './AuditFindingsManagement.styled';

type IPrioritizationItem = {
  options: string;
};

type IAuditSource = {
  options: string;
};

const { useToken } = theme;

const AuditFindingsManagement: React.FC = () => {
  const { token } = useToken();
  const dispatch = useDispatch();

  const {
    drawTable,
    lang,
    mgmtAudits,
    filtersData,
    saveClicked,
    orgContext,
    modules
  } = useSelector((state: RootState) => ({
    drawTable: state.management.drawTable,
    lang: state.global.lang,
    mgmtAudits: state.management.mgmtAudits,
    filtersData: state.management.filtersData,
    saveClicked: state.management.saveClicked,
    orgContext: state.global.hierarchicalLevelsScope,
    modules: state.management.modules
  }));

  const [prioritizationData, setPrioritizationData] = useState<
    IPrioritizationItem[]
  >([]);

  const [auditSourceData, setAuditSourceData] = useState<IAuditSource[]>([]);
  const [ownersList, setOwnersList] = useState<{ id: number; email: string }[]>(
    []
  );
  const filterParams = Helpers.toApiFilters(orgContext);
  const [rows, setRows] = useState<IAuditFindingsResult[]>([]);
  const [modifiedRowIds, setModifiedRowIds] = useState<Set<number>>(new Set());
  const [, forceUpdate] = useState({});
  const [initialRows, setInitialRows] = useState<IAuditFindingsResult[]>([]);
  const [initialModifiedRowIds, setInitialModifiedRowIds] = useState<
    Set<number>
  >(new Set());

  const mapOptions = (data: any[] = []) =>
    data.map((item) => ({
      value: item.id,
      label: item.name
    }));

  const hasChanges = useMemo(() => {
    if (initialRows.length === 0 && rows.length === 0) {
      return false;
    }
    const rowsChanged = JSON.stringify(rows) !== JSON.stringify(initialRows);
    const hasModifiedRows = modifiedRowIds.size > 0;
    return rowsChanged || hasModifiedRows;
  }, [rows, initialRows, modifiedRowIds]);

  useUnsavedChanges({ hasChanges });

  useEffect(() => {
    i18n.changeLanguage(lang);
    forceUpdate({});
  }, [lang]);

  const handleAddRow = () => {
    const newRow: IAuditFindingsResult = {
      key: generateUniqueId(),
      afg_id: generateUniqueId(),
      ads_value_en: '',
      afg_desc_en: '',
      afg_name_en: '',
      audit_name: '',
      afg_due_date: null,
      afg_owner_ref: 0,
      per_email: '',
      pri_value_en: ''
      // afg_ind_ref: null,
      // afg_mod_ref: null,
    };
    setRows((prevRows) => [...prevRows, newRow]);
    dispatch(managementOp.setDataEdited(true));
  };

  const handleFieldChange = (
    rowKey: string | number,
    field: string,
    value: string | number | null
  ) => {
    setRows((prevRows) =>
      prevRows.map((row) => {
        if (row.key === rowKey) {
          if (row.afg_id > 0) {
            setModifiedRowIds((prev) => new Set(prev).add(row.afg_id));
          }
          return { ...row, [field]: value };
        }
        return row;
      })
    );

    dispatch(managementOp.setDataEdited(true));
  };

  const getModuleLabel = (module: any) => {
    if (!module) return null;

    if (typeof module === 'object' && module.label) {
      return module.label;
    }

    return modules?.find((m: any) => m.value === module)?.label ?? null;
  };

  const getCount = (value: any) =>
    Array.isArray(value) ? value.length : value ? 1 : 0;

  useEffect(() => {
    if (!saveClicked) return;

    // Industry validation
    if (getCount(orgContext.industryType) > 1) {
      notification.error({
        message: 'Please select only one Industry in the "Who am I" area.'
      });

      dispatch(managementOp.setSaveClicked(false));
      return;
    }

    // Module validation
    if (getCount(orgContext.module) > 1) {
      notification.error({
        message: 'Please select only one Module in the "Who am I" area.'
      });

      dispatch(managementOp.setSaveClicked(false));
      return;
    }

    // Group / BU / LE / Region / Division validation
    const hierarchyCount =
      getCount(orgContext.group) +
      getCount(orgContext.businessUnit) +
      getCount(orgContext.legalEntity) +
      getCount(orgContext.region) +
      getCount(orgContext.division);

    if (hierarchyCount > 1) {
      notification.error({
        message:
          'Please select only one of Group, Business Unit, Legal Entity, Region, or Division in the "Who am I" area.'
      });

      dispatch(managementOp.setSaveClicked(false));
      return;
    }

    const dataToSave = rows.map((row) => ({
      afg_id: row.afg_id,
      ads_value_en: row.ads_value_en,
      afg_desc_en: row.afg_desc_en,
      audit_name: row.afg_name_en || '',

      afg_due_date: row.afg_due_date,
      afg_owner_ref: row.afg_owner_ref ?? 0,

      pri_value_en: row.pri_value_en,

      ind_value_en:
        (
          orgContext?.industryType as
            | { label: string; value: number; key: number }[]
            | undefined
        )?.[0]?.label ?? null,

      mod_value_en: Array.isArray(orgContext.module)
        ? getModuleLabel(orgContext.module[0])
        : getModuleLabel(orgContext.module),

      group_id: Array.isArray(orgContext.group)
        ? orgContext.group[0]
        : orgContext.group ?? null,

      division_id: Array.isArray(orgContext.division)
        ? orgContext.division[0]
        : orgContext.division ?? null,

      region_id: Array.isArray(orgContext.region)
        ? orgContext.region[0]
        : orgContext.region ?? null,

      legal_entity_id: Array.isArray(orgContext.legalEntity)
        ? orgContext.legalEntity[0]
        : orgContext.legalEntity ?? null,

      business_unit_id: Array.isArray(orgContext.businessUnit)
        ? orgContext.businessUnit[0]
        : orgContext.businessUnit ?? null
    }));

    managementOp
      .saveAuditFindings(dataToSave, lang, Array.from(modifiedRowIds))
      .then(() => {
        dispatch(managementOp.setSaveClicked(false));
        dispatch(managementOp.setDataEdited(false));
        setModifiedRowIds(new Set());
        setInitialRows(rows);
        setInitialModifiedRowIds(new Set());
      })
      .catch((error) => {
        console.error(error);
        dispatch(managementOp.setSaveClicked(false));
      });
  }, [saveClicked, rows, dispatch, modifiedRowIds, orgContext, modules, lang]);

  useEffect(() => {
    if (mgmtAudits && Array.isArray(mgmtAudits)) {
      const newRows = mgmtAudits.map((audit: any, index: number) => ({
        key: generateUniqueId(),
        afg_id: audit.afg_id ?? 0,
        ads_value_en: audit.ads_value_en,
        afg_desc_en: audit.afg_desc_en,
        afg_name_en:
          audit.audit_name && audit.audit_name !== 'None'
            ? audit.audit_name
            : audit.afg_name_en || '',

        afg_due_date: audit.afg_due_date,
        afg_owner_ref: audit.afg_owner_ref,
        per_email: audit.per_email,
        pri_value_en: audit.pri_value_en,
        afg_ind_ref: audit.afg_ind_ref,
        afg_mod_ref: audit.afg_mod_ref
      }));

      setRows(newRows);

      if (newRows.length > 0) {
        setInitialRows(newRows);
        setInitialModifiedRowIds(new Set());
      }

      managementOp
        .fetchMngmPrioritization(lang)
        .then((response) => {
          if (response && response.prioritization) {
            const sorted = [...response.prioritization].sort((a, b) =>
              a.localeCompare(b)
            );
            const transformedData: IPrioritizationItem[] = sorted.map(
              (priority: string) => ({
                options: priority
              })
            );
            setPrioritizationData(transformedData);
          }
        })
        .catch((error) => {
          console.error('Error fetching prioritization data:', error);
        });

      managementOp
        .fetchAuditSource(lang)
        .then((response) => {
          if (response && response.audit_sources) {
            const sorted = [...response.audit_sources].sort((a, b) =>
              a.localeCompare(b)
            );
            const transformedDataAuditSource: IAuditSource[] = sorted.map(
              (aud_source: string) => ({
                options: aud_source
              })
            );
            setAuditSourceData(transformedDataAuditSource);
          }
        })
        .catch((error) => {
          console.error('Error fetching audit source data:', error);
        });

      const userToken = JSON.parse(
        sessionStorage.getItem('user') || '{}'
      )?.token;
      if (userToken) {
        managementOp
          .getOwnersList()
          .then((owners: { per_person_id: number; per_email: string }[]) => {
            const ownerOptions = owners.map((owner) => ({
              id: owner.per_person_id,
              email: owner.per_email
            }));
            const sorted = [...ownerOptions].sort((a, b) =>
              a.email.localeCompare(b.email)
            );
            setOwnersList(sorted);
          });
      }
    }
  }, [mgmtAudits, lang]);

  const handleDeleteClick = async (record: IAuditFindingsResult) => {
    if (!record.afg_id) return;

    try {
      await managementOp.deleteAuditFindings({ afg_id: record.afg_id });
      setRows((prevRows) =>
        prevRows.filter((row) => row.afg_id !== record.afg_id)
      );
    } catch (error) {
      console.error('Failed to delete audit finding:', error);
    }
  };

  const cancel = (e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    message.error(i18n.t('AUD_FINDING_NOT_DELETED'));
  };

  const columns: ColumnsType<IAuditFindingsResult> = [
    {
      title: i18n.t('ACTION'),
      key: 'action',
      render: (_, record, index) => (
        <Styled.StyledButtonSection token={token}>
          <Popconfirm
            title={i18n.t('DELETE_AUDIT_FINDING')}
            description={i18n.t('CONFIRM_DELETE_AUDIT_FINDING')}
            onConfirm={() => handleDeleteClick(record)}
            onCancel={cancel}
            okText={i18n.t('YES')}
            cancelText={i18n.t('NO')}
          >
            <Button
              type='primary'
              size='small'
              shape='circle'
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Styled.StyledButtonSection>
      )
    },

    {
      title: i18n.t('AUDIT_FINDING_NAME'),
      dataIndex: 'afg_name_en',
      key: 'afg_name_en',
      width: 200,
      render: (_, data) => (
        <Styled.ColumnTextBox
          width={200}
          defaultValue={data.afg_name_en}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleFieldChange(data.key, 'afg_name_en', e.target.value)
          }
        />
      )
    },

    {
      title: i18n.t('AUDIT_FINDING_DESC'),
      dataIndex: 'afg_desc_en',
      key: 'afg_desc_en',
      width: 200,
      render: (_, data) => (
        <Styled.ColumnTextBox
          width={200}
          defaultValue={data.afg_desc_en}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleFieldChange(data.key, 'afg_desc_en', e.target.value)
          }
        />
      )
    },
    // PRIORITY
    {
      title: i18n.t('PRIORITY'),
      dataIndex: 'pri_value_en',
      key: 'pri_value_en',
      width: 150,
      render: (_, data) => {
        const prData = prioritizationData.map((item) => ({
          value: item.options,
          label: item.options
        }));

        return (
          <CustomSelect
            showSearch
            options={prData}
            value={data.pri_value_en}
            onChange={(value: string) =>
              handleFieldChange(data.key, 'pri_value_en', value)
            }
          />
        );
      }
    },

    // AUDIT SOURCE
    {
      title: i18n.t('AUDIT_SOURCE'),
      dataIndex: 'ads_value_en',
      key: 'ads_value_en',
      width: 300,
      render: (_, data) => {
        const audSrcData = auditSourceData.map((item) => ({
          value: item.options,
          label: item.options
        }));

        return (
          <CustomSelect
            showSearch
            options={audSrcData}
            value={data.ads_value_en}
            onChange={(value: string) =>
              handleFieldChange(data.key, 'ads_value_en', value)
            }
          />
        );
      }
    },

    // DUE DATE
    {
      title: i18n.t('AUDIT_DUE_DATE'),
      dataIndex: 'afg_due_date',
      key: 'afg_due_date',
      width: 150,
      render: (_, data) => {
        const formatted = data.afg_due_date
          ? new Date(data.afg_due_date).toISOString().split('T')[0]
          : null;

        return (
          <CustomDatePicker
            defaultValue={formatted}
            onChange={(date: string | null) =>
              handleFieldChange(data.key, 'afg_due_date', date)
            }
          />
        );
      }
    },

    // OWNER
    {
      title: i18n.t('AUDIT_OWNER'),
      dataIndex: 'afg_owner_ref',
      key: 'afg_owner_ref',
      width: 150,
      render: (_, data) => (
        <CustomSelect
          showSearch
          options={ownersList.map((owner) => ({
            value: owner.id,
            label: owner.email
          }))}
          value={data.afg_owner_ref || undefined}
          onChange={(value: number | undefined) =>
            handleFieldChange(data.key, 'afg_owner_ref', value ?? null)
          }
        />
      )
    }
  ];

  return (
    <DataManagementLayout>
      <Styled.ContentWrapper>
        {drawTable ? (
          <>
            <CustomTable
              drawTable={drawTable}
              columns={columns}
              dataSource={rows}
              itemsCount={rows.length}
              pagination={false}
              scroll={{ x: 1000 }}
            />
            <Styled.AddButton
              type='primary'
              onClick={handleAddRow}
              icon={<PlusOutlined />}
              shape='circle'
            />
          </>
        ) : (
          <CustomTable
            drawTable={drawTable}
            itemsCount={0}
            columns={columns}
            dataSource={[]}
            pagination={false}
          />
        )}
      </Styled.ContentWrapper>
    </DataManagementLayout>
  );
};

export default AuditFindingsManagement;
